import * as Sentry from '@sentry/react';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const ResetErrorOnNavigation = ({
  children,
  resetError
}: PropsWithChildren<{ resetError: () => void }>) => {
  const { pathname } = useLocation();
  const previousPathname = useRef(pathname);

  useEffect(() => {
    if (previousPathname.current !== pathname) {
      previousPathname.current = pathname;
      resetError();
    }
  }, [pathname, resetError]);

  return <>{children}</>;
};

// Only templates preserve their shell across navigation. Other pages rely on
// remounting to reset entity-specific forms and local state when the URL changes.
const RouteErrorBoundary = ({ fallback, ...props }: Sentry.ErrorBoundaryProps) => {
  const { pathname } = useLocation();
  const boundaryKey =
    pathname === '/admin/templates' || pathname.startsWith('/admin/templates/')
      ? '/admin/templates'
      : pathname;
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;
  // Sentry mounts a function fallback as a component. Keep its identity stable
  // so navigation does not remount its path tracker before it can reset the error.
  const renderFallback = useCallback<Sentry.FallbackRender>(
    (errorData) => (
      <ResetErrorOnNavigation resetError={errorData.resetError}>
        {typeof fallbackRef.current === 'function'
          ? fallbackRef.current(errorData)
          : fallbackRef.current}
      </ResetErrorOnNavigation>
    ),
    []
  );

  return <Sentry.ErrorBoundary {...props} key={boundaryKey} fallback={renderFallback} />;
};

export default RouteErrorBoundary;
